// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
// Avisa o usuário quando há uma nova versão do aplicativo publicada.
import { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';

const CHECK_INTERVAL_MS = 30 * 60 * 1000;

export default function UpdateNotifier() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [reloading, setReloading] = useState(false);

  const checkForUpdate = useCallback(async () => {
    try {
      const res = await fetch(`/asset-manifest.json?ts=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const manifest = await res.json();
      const latest = manifest.files?.['main.js'];
      const current = Array.from(document.scripts)
        .map((s) => s.src)
        .find((s) => s.includes('/static/js/main.'));
      if (latest && current && !current.includes(latest)) {
        setUpdateAvailable(true);
      }
    } catch { /* offline ou preview sem build — ignora */ }
  }, []);

  useEffect(() => {
    checkForUpdate();
    const onVisible = () => { if (document.visibilityState === 'visible') checkForUpdate(); };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(checkForUpdate, CHECK_INTERVAL_MS);
    return () => { document.removeEventListener('visibilitychange', onVisible); clearInterval(timer); };
  }, [checkForUpdate]);

  const handleReload = async () => {
    setReloading(true);
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      const reg = await navigator.serviceWorker?.getRegistration();
      await reg?.update();
    } catch { /* segue para o reload */ }
    window.location.reload();
  };

  if (!updateAvailable) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[100] flex items-center justify-center gap-3 px-4 py-2.5 bg-primary text-primary-foreground shadow-lg"
      data-testid="update-banner"
    >
      <Sparkles className="w-4 h-4 shrink-0" />
      <span className="text-sm font-medium">Nova atualização disponível!</span>
      <button
        onClick={handleReload}
        disabled={reloading}
        className="flex items-center gap-1.5 text-sm font-semibold underline underline-offset-2 disabled:opacity-70"
        data-testid="update-reload-btn"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${reloading ? 'animate-spin' : ''}`} />
        {reloading ? 'Atualizando...' : 'Atualizar agora'}
      </button>
    </div>
  );
}
